<template>

    <main
        id="project-not-found"
        v-if="!fullContent"
    >

        <div class="container">

            <div class="project-not-found__content">

                <span>
                    404
                </span>

                <h1>
                    {{ 'Project not found' | i }}
                </h1>

                <p>
                    {{ 'We could not find the information for this project.' | i }}
                </p>

                <router-link
                    to="/"
                    class="project-not-found__button"
                >
                    {{ 'Back to home' | i }}
                </router-link>

            </div>

        </div>

    </main>

    <main
        id="project-detail"
        class="project-detail"
        v-else
    >

        <section class="project-hero">

            <div class="project-hero__circle project-hero__circle--one"></div>
            <div class="project-hero__circle project-hero__circle--two"></div>

            <div class="container">

                <div class="project-hero__grid">

                    <div class="project-hero__content">

                        <span class="project-hero__eyebrow">
                            {{ 'DANTRIX PRO / PROJECT' | i }}
                        </span>

                        <h1>
                            {{ fullContent.title }}
                        </h1>

                        <p v-if="fullContent.content_alt">
                            {{ fullContent.content_alt }}
                        </p>

                    </div>

                    <div
                        v-if="fullContent.imagenes_id"
                        class="project-hero__media"
                    >

                        <img
                            :src="fullContent.imagenes_id | asImg"
                            :alt="fullContent.title"
                        >

                        <span class="project-hero__media-label">
                            {{ 'CASE STUDY' | i }}
                        </span>

                    </div>

                </div>

            </div>

        </section>

        <section class="project-content">

            <div class="container">

                <div
                    class="project-content__layout"
                    :class="{
                        'project-content__layout--with-aside':
                            displayAside
                    }"
                >

                    <aside
                        v-if="displayAside"
                        class="project-content__aside"
                    >

                        <span class="project-content__index">
                            {{ '01 / PROJECT' | i }}
                        </span>

                        <h2>
                            {{ 'BEHIND THE PROJECT' | i }}
                        </h2>

                        <p>
                            {{ 'Every solution starts by understanding the business, its needs and the goals it wants to achieve.' | i }}
                        </p>

                        <div class="project-content__aside-line"></div>

                        <router-link
                            :to="{
                                name: 'Home',
                                hash: '#contact'
                            }"
                            class="project-content__contact"
                        >
                            {{ 'LET’S TALK ABOUT YOUR PROJECT' | i }}

                            <span>
                                ↗
                            </span>
                        </router-link>

                    </aside>

                    <article class="project-content__article">

                        <vue-marked>
                            {{ fullContent.content }}
                        </vue-marked>

                    </article>

                </div>

            </div>

        </section>

        <section class="project-footer">

            <div class="container">

                <div class="project-footer__box">

                    <span>
                        {{ 'DANTRIX PRO / NEXT STEP' | i }}
                    </span>

                    <h2>
                        {{ 'DO YOU HAVE AN IDEA IN MIND?' | i }}
                    </h2>

                    <p>
                        {{ 'We can analyze your project and develop a solution adapted to what your business needs.' | i }}
                    </p>

                    <router-link
                        :to="{
                            name: 'Home',
                            hash: '#contact'
                        }"
                        class="project-footer__button"
                    >
                        {{ 'Connect with us' | i }}

                        <span>
                            ↗
                        </span>
                    </router-link>

                </div>

            </div>

        </section>

        <div
            v-if="debug"
            class="debug"
        >
            <pre>{{ fullContent }}</pre>
        </div>

    </main>

</template>

<script>
/* eslint-disable */

import { mapGetters } from 'vuex';

export default {
    name: 'Article',

    props: {
        debug: {
            type: Boolean,
            default: false
        },

        displayAside: {
            type: Boolean,
            default: false
        }
    },

    computed: {
        ...mapGetters([
            'catalogPagesContent',
            'catalogPortfolioContent'
        ])
    },

    data() {
        return {
            fullContent: null
        };
    },

    methods: {
        routeUpdate() {
            this.fullContent = null;

            try {
                if (this.$route.params.hash) {
                    this.fullContent =
                        this.getContentFromHash();
                } else if (this.$route.params.id) {
                    this.fullContent =
                        this.getPortfolioFromID();
                }

                if (!this.fullContent) {
                    throw new Error(
                        'Content not found'
                    );
                }
            } catch (error) {
                console.error(
                    'Error reviewing content:',
                    error
                );
            } finally {
                document.title =
                    'DANTRIX PRO | ' +
                    (
                        this.fullContent?.title ||
                        this.$route.params.hash ||
                        'Project'
                    );
            }
        },

        getContentFromHash() {
            const hash =
                String(
                    this.$route.params.hash || ''
                )
                    .trim()
                    .toLowerCase();

            return Array.isArray(
                this.catalogPagesContent
            )
                ? this.catalogPagesContent.find(
                    content =>
                        String(
                            content.hash || ''
                        )
                            .trim()
                            .toLowerCase() === hash
                )
                : null;
        },

        getPortfolioFromID() {
            const id =
                this.$route.params.id;

            return Array.isArray(
                this.catalogPortfolioContent
            )
                ? this.catalogPortfolioContent.find(
                    content =>
                        String(content.id) ===
                        String(id)
                )
                : null;
        }
    },

    mounted() {
        if (
            window.mainUI &&
            typeof window.mainUI === 'function'
        ) {
            setTimeout(
                window.mainUI,
                150
            );
        }
    },

    watch: {
        '$route.params.hash'() {
            this.routeUpdate();
        },

        '$route.params.id'() {
            this.routeUpdate();
        }
    },

    beforeMount() {
        this.routeUpdate();
    }
};
</script>

<style lang="scss" scoped>
#project-not-found {
    min-height: 80vh;

    display: flex;
    align-items: center;
    justify-content: center;

    padding:
        8em 1.25em;

    background:
        var(--color-1);

    text-align: center;
}

.project-not-found__content {
    max-width:
        38em;

    margin:
        0 auto;

    > span {
        display: block;

        margin-bottom:
            .25em;

        color:
            var(--color-3);

        font-family:
            var(--font-tech);

        font-size:
            clamp(
                4em,
                10vw,
                8em
            );

        font-weight:
            700;

        line-height:
            1;
    }

    h1 {
        margin:
            0 0
            .75em;

        color:
            var(--text-1);

        font-size:
            clamp(
                2em,
                5vw,
                3.5em
            );

        text-transform:
            uppercase;
    }

    p {
        margin:
            0 0
            2em;

        color:
            var(--text-2);

        line-height:
            1.8;
    }
}

.project-not-found__button {
    display:
        inline-flex;

    padding:
        1em 1.5em;

    border:
        1px solid
        var(--color-3);

    color:
        var(--text-1);

    font-family:
        var(--font-tech);

    font-size:
        .7em;

    letter-spacing:
        .12em;

    text-decoration:
        none;

    text-transform:
        uppercase;

    transition:
        .3s ease;

    &:hover {
        background:
            var(--color-3);
    }
}

.project-detail {
    min-height:
        100vh;

    background:
        var(--color-1);

    color:
        var(--text-1);
}

.container {
    width:
        100%;

    max-width:
        var(--container);

    margin:
        0 auto;
}

.project-hero {
    position:
        relative;

    padding:
        9em 1.25em
        6em;

    background:
        radial-gradient(
            circle at 15% 20%,
            rgba(233, 81, 29, .045),
            transparent 28%
        ),
        var(--color-2);

    border-bottom:
        1px solid
        var(--border-1);

    overflow:
        hidden;
}

.project-hero__circle {
    position:
        absolute;

    border-radius:
        50%;

    pointer-events:
        none;

    &--one {
        top:
            -18em;

        right:
            -14em;

        width:
            36em;

        height:
            36em;

        border:
            1px solid
            rgba(255, 255, 255, .035);
    }

    &--two {
        bottom:
            -24em;

        left:
            -18em;

        width:
            34em;

        height:
            34em;

        border:
            1px solid
            rgba(233, 81, 29, .06);
    }
}

.project-hero__grid {
    position:
        relative;

    display:
        grid;

    grid-template-columns:
        1fr;

    gap:
        3em;

    align-items:
        center;

    z-index:
        2;
}

.project-hero__content {
    h1 {
        max-width:
            10em;

        margin:
            0 0
            .4em;

        color:
            var(--text-1);

        font-size:
            clamp(
                3em,
                8vw,
                6.5em
            );

        font-weight:
            700;

        line-height:
            .92;

        letter-spacing:
            -.04em;

        text-transform:
            uppercase;
    }

    p {
        max-width:
            42em;

        margin:
            0;

        color:
            var(--text-2);

        font-size:
            .95em;

        line-height:
            1.8;
    }
}

.project-hero__eyebrow {
    display:
        inline-flex;

    align-items:
        center;

    gap:
        .8em;

    margin-bottom:
        1.8em;

    color:
        var(--text-3);

    font-family:
        var(--font-tech);

    font-size:
        .62em;

    font-weight:
        600;

    letter-spacing:
        .16em;

    text-transform:
        uppercase;

    &::before {
        content: "";

        width:
            2.5em;

        height:
            1px;

        background:
            var(--color-3);
    }
}

.project-hero__media {
    position:
        relative;

    min-height:
        18em;

    overflow:
        hidden;

    border:
        1px solid
        var(--border-1);

    background:
        var(--color-1);

    img {
        display:
            block;

        width:
            100%;

        height:
            100%;

        min-height:
            18em;

        object-fit:
            cover;
    }
}

.project-hero__media-label {
    position:
        absolute;

    left:
        1.25em;

    bottom:
        1.25em;

    padding:
        .8em 1em;

    background:
        rgba(1, 16, 36, .82);

    border:
        1px solid
        rgba(255, 255, 255, .12);

    color:
        var(--text-3);

    font-family:
        var(--font-tech);

    font-size:
        .58em;

    letter-spacing:
        .12em;
}

.project-content {
    padding:
        6em 1.25em
        7em;

    background:
        var(--color-1);
}

.project-content__layout {
    display:
        grid;

    grid-template-columns:
        minmax(0, 1fr);

    max-width:
        74em;

    margin:
        0 auto;
}

.project-content__layout--with-aside {
    gap:
        4em;
}

.project-content__aside {
    h2 {
        margin:
            .8em 0
            .6em;

        color:
            var(--text-1);

        font-size:
            clamp(
                1.8em,
                4vw,
                2.8em
            );

        line-height:
            1;

        text-transform:
            uppercase;
    }

    p {
        max-width:
            28em;

        margin:
            0;

        color:
            var(--text-2);

        font-size:
            .9em;

        line-height:
            1.8;
    }
}

.project-content__index {
    color:
        var(--text-3);

    font-family:
        var(--font-tech);

    font-size:
        .6em;

    letter-spacing:
        .15em;
}

.project-content__aside-line {
    width:
        100%;

    height:
        1px;

    margin:
        2em 0;

    background:
        var(--border-1);
}

.project-content__contact {
    display:
        flex;

    align-items:
        center;

    justify-content:
        space-between;

    gap:
        1em;

    padding:
        1em 0;

    color:
        var(--text-1);

    font-family:
        var(--font-tech);

    font-size:
        .65em;

    font-weight:
        600;

    letter-spacing:
        .12em;

    text-decoration:
        none;

    span {
        color:
            var(--text-3);

        font-size:
            1.5em;
    }

    &:hover {
        color:
            var(--text-3);
    }
}

.project-content__article {
    width:
        100%;

    max-width:
        68em;

    margin:
        0 auto;

    ::v-deep h1 {
        margin:
            0 0
            .8em;

        color:
            var(--text-1);

        font-size:
            clamp(
                2.2em,
                5vw,
                4em
            );

        line-height:
            1;

        text-transform:
            uppercase;
    }

    ::v-deep h2 {
        margin:
            2em 0
            .7em;

        color:
            var(--text-1);

        font-size:
            clamp(
                1.7em,
                4vw,
                2.6em
            );

        font-weight:
            600;

        line-height:
            1.1;

        text-transform:
            uppercase;

        &:first-child {
            margin-top:
                0;
        }
    }

    ::v-deep h3 {
        margin:
            2em 0
            .75em;

        color:
            var(--text-3);

        font-size:
            1.15em;

        font-weight:
            600;

        text-transform:
            uppercase;
    }

    ::v-deep p {
        margin:
            0 0
            1.5em;

        color:
            var(--text-2);

        font-size:
            1em;

        line-height:
            1.9;
    }

    ::v-deep ul,
    ::v-deep ol {
        margin:
            1.5em 0;

        padding-left:
            1.5em;

        color:
            var(--text-2);

        line-height:
            1.9;
    }

    ::v-deep li {
        margin-bottom:
            .5em;
    }

    ::v-deep strong {
        color:
            var(--text-1);
    }

    ::v-deep a {
        color:
            var(--text-3);

        text-decoration:
            none;

        border-bottom:
            1px solid
            rgba(233, 81, 29, .35);
    }

    ::v-deep blockquote {
        margin:
            2.5em 0;

        padding:
            1.5em 1.8em;

        border-left:
            3px solid
            var(--color-3);

        background:
            rgba(255, 255, 255, .025);

        color:
            var(--text-1);

        font-size:
            1.15em;

        line-height:
            1.8;
    }

    ::v-deep img {
        display:
            block;

        width:
            100%;

        margin:
            2.5em 0;

        border:
            1px solid
            var(--border-1);

        object-fit:
            cover;
    }

    ::v-deep hr {
        height:
            1px;

        margin:
            3em 0;

        border:
            0;

        background:
            var(--border-1);
    }
}

.project-footer {
    padding:
        0 1.25em
        7em;

    background:
        var(--color-1);
}

.project-footer__box {
    max-width:
        74em;

    margin:
        0 auto;

    padding:
        4em 2em;

    text-align:
        center;

    border:
        1px solid
        var(--border-1);

    background:
        radial-gradient(
            circle at 50% 0%,
            rgba(233, 81, 29, .08),
            transparent 40%
        ),
        var(--color-2);

    > span {
        display:
            block;

        margin-bottom:
            1.4em;

        color:
            var(--text-3);

        font-family:
            var(--font-tech);

        font-size:
            .6em;

        letter-spacing:
            .16em;
    }

    h2 {
        max-width:
            12em;

        margin:
            0 auto
            .5em;

        color:
            var(--text-1);

        font-size:
            clamp(
                2em,
                5vw,
                4em
            );

        line-height:
            .95;

        text-transform:
            uppercase;
    }

    p {
        max-width:
            38em;

        margin:
            0 auto
            2em;

        color:
            var(--text-2);

        line-height:
            1.8;
    }
}

.project-footer__button {
    display:
        inline-flex;

    align-items:
        center;

    gap:
        1em;

    padding:
        1.1em 1.5em;

    background:
        var(--color-3);

    border:
        1px solid
        var(--color-3);

    color:
        var(--text-1);

    font-family:
        var(--font-tech);

    font-size:
        .68em;

    font-weight:
        600;

    letter-spacing:
        .12em;

    text-decoration:
        none;

    text-transform:
        uppercase;

    span {
        font-size:
            1.4em;
    }

    &:hover {
        background:
            transparent;

        color:
            var(--text-3);
    }
}

@media (min-width: 850px) {
    .project-hero {
        padding:
            10em 3em
            7em;
    }

    .project-hero__grid {
        grid-template-columns:
            minmax(0, .9fr)
            minmax(0, 1.1fr);

        gap:
            5em;
    }

    .project-hero__media {
        min-height:
            30em;

        img {
            min-height:
                30em;
        }
    }

    .project-content {
        padding:
            7em 3em
            8em;
    }

    .project-content__layout--with-aside {
        grid-template-columns:
            minmax(14em, .55fr)
            minmax(0, 1.45fr);

        gap:
            6em;
    }

    .project-content__layout--with-aside
    .project-content__aside {
        position:
            sticky;

        top:
            8em;

        align-self:
            start;
    }

    .project-content__layout--with-aside
    .project-content__article {
        max-width:
            none;
    }

    .project-footer {
        padding:
            0 3em
            8em;
    }

    .project-footer__box {
        padding:
            5em 3em;
    }
}
</style>